import { useEffect, useRef } from "react";
import { usePodgoVersion } from "@/hooks/usePodgoVersion";
import { PODGO_VERSION_FALLBACK } from "@/version";

const POLL_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Silently polls the DB for the current PODGO version and force-reloads
 * the tab when a newer version is detected. No UI — bypass HTTP cache to
 * fetch a fresh bundle so users are never stuck on a stale build.
 */
export function VersionUpdatePrompt() {
  const { version } = usePodgoVersion({ pollMs: POLL_MS });
  const bootVersionRef = useRef<string | null>(null);
  const reloadedRef = useRef(false);

  useEffect(() => {
    if (!version || version === PODGO_VERSION_FALLBACK) return;

    if (bootVersionRef.current === null) {
      bootVersionRef.current = version;
      return;
    }

    if (version !== bootVersionRef.current && !reloadedRef.current) {
      reloadedRef.current = true;
      // Cache-bust: stable per-deploy token avoids reload loops while
      // forcing intermediaries to treat this as a fresh URL.
      const url = new URL(window.location.href);
      url.searchParams.set("v", version);
      window.location.replace(url.toString());
    }
  }, [version]);

  return null;
}
